import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { ExploreHeader, ExploreHolding, ExploreError, ExploreSecondaryLink } from "@/attendee/ExploreShared";
import { ChevronDown, LogOut, Plane, BookOpen, Building2, Ticket } from "lucide-react";

export default function ExploreHotel() {
  const navigate = useNavigate();
  const { eventId } = useApp();
  const [items, setItems] = useState(null);
  const [state, setState] = useState("loading");
  const [open, setOpen] = useState({});

  const load = useCallback(() => {
    let active = true;
    setState("loading");
    setItems(null);
    api.exploreHotel(eventId)
      .then((d) => { if (active) { if (d.activated === false) { setState("holding"); } else { setItems(d.items || []); setState("ok"); } } })
      .catch(() => { if (active) setState("error"); });
    return () => { active = false; };
  }, [eventId]);

  useEffect(() => load(), [load]);

  if (state === "error") return <ExploreError onRetry={load} />;
  if (state === "holding") return <ExploreHolding />;
  if (!items) return <div className="pt-20 px-4"><div className="h-48 att-card animate-pulse" /></div>;

  const hotel = items.find((i) => i.type === "hotel_essentials");
  const dmc = items.find((i) => i.type === "dmc_info");

  return (
    <div className="pt-16 px-4 pb-8">
      <ExploreHeader eyebrow="Explore" title="Hotel Essentials" subtitle={hotel?.subtitle} />

      {hotel?.checkout && (
        <div className="att-card p-4 mb-3 flex items-center gap-3" data-testid="hotel-checkout">
          <LogOut className="h-5 w-5 text-[hsl(var(--pc-gold))]" />
          <div><div className="text-sm text-cream font-semibold">Checkout {hotel.checkout}</div></div>
        </div>
      )}

      {/* Amstar desk */}
      {(dmc || hotel?.amstar_desk) && (
        <div className="att-card p-4 mb-3" data-testid="hotel-amstar">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-[hsl(var(--pc-gold))] mb-1 inline-flex items-center gap-1.5"><Ticket className="h-3.5 w-3.5" /> {dmc?.name || "Amstar"} hospitality desk</div>
          <p className="text-sm text-cream leading-relaxed">{dmc?.booking_note || hotel?.amstar_desk}</p>
          {dmc?.location && <p className="text-[11px] text-cream-dim mt-1">{dmc.location}</p>}
        </div>
      )}

      {hotel?.overview && (
        <div className="att-card p-4 mb-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.3)] shrink-0"><Building2 className="h-5 w-5" /></div>
            <p className="text-sm text-cream-dim leading-relaxed">
              {hotel.overview}{" "}For your personalized travel, stay and coverage details, visit{" "}
              <button onClick={() => navigate("/app/more?tab=kbyg")} className="text-[hsl(var(--pc-gold))] font-semibold underline" data-testid="hotel-overview-kbyg">Know Before You Go</button>{" "}or{" "}
              <button onClick={() => navigate("/app/trip")} className="text-[hsl(var(--pc-gold))] font-semibold underline" data-testid="hotel-overview-mytrip">My Trip</button>.
            </p>
          </div>
        </div>
      )}

      {/* Policy sections */}
      {hotel?.sections?.length > 0 && (
        <div className="space-y-2 mb-3">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-cream-dim px-1">Hotel policies</div>
          {hotel.sections.map((s, i) => (
            <div key={i} className="att-card overflow-hidden" data-testid={`hotel-section-${i}`}>
              <button onClick={() => setOpen((p) => ({ ...p, [i]: !p[i] }))} className="w-full flex items-center justify-between px-4 py-3 text-left" aria-expanded={!!open[i]}>
                <span className="font-semibold text-cream text-sm pr-3">{s.heading}</span>
                <ChevronDown className={`h-4 w-4 text-[hsl(var(--pc-gold))] shrink-0 transition-transform duration-200 ${open[i] ? "rotate-180" : ""}`} />
              </button>
              {open[i] && <p className="px-4 pb-4 pt-1 border-t border-[hsl(var(--pc-line))] text-sm text-cream-dim leading-relaxed">{s.body}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <ExploreSecondaryLink icon={BookOpen} title="Know Before You Go" subtitle="Your travel, entry & guest guidance" onClick={() => navigate("/app/more?tab=kbyg")} testid="hotel-link-kbyg" />
        <ExploreSecondaryLink icon={Plane} title="My Trip" subtitle="Your flights & personal transfers" onClick={() => navigate("/app/trip")} testid="hotel-link-mytrip" />
      </div>
    </div>
  );
}
